import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  MapPin,
  Merge,
  Undo2,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LoadFailureNote from "../components/LoadFailureNote";
import PairMap from "../components/PairMap";
import { placeTypeLabel } from "../lib/placeType";
import {
  bandMeta,
  dismissDuplicatePair,
  explainPair,
  fetchDuplicateGroundReport,
  formatMetres,
  formatReportCounts,
  formatScanSummary,
  formatStaleVerdict,
  formatVerdict,
  groupByBand,
  restoreDuplicatePair,
} from "../lib/groundDuplicates";
import {
  MERGE_UNDO_WARNING,
  canMerge,
  describeMergeDirection,
  describeMergeImpact,
  describeMergeResult,
  describeSurvivorChoice,
  fetchPairMergeImpact,
  mergeDirection,
  mergeGroundPair,
  survivorChoices,
} from "../lib/groundMerge";
import { venueRecordPath } from "../lib/adminVenues";
import { formatApiErrorDetail } from "../lib/api";

/**
 * `/admin/grounds` — the Grounds section of the one admin area (issue
 * #132), a third section on the pattern #219 set and #226 confirmed:
 *
 *   /admin
 *   ├── Guides   <- AdminGuides.jsx (#219)
 *   ├── Users    <- AdminUsers.jsx (#226)
 *   └── Grounds  <- here (#132)
 *
 * Martin, Discord, 2026-08-05: "We should also get a report where we have
 * duplicate fields with slightly different spelling or coordinates and seek
 * AI or human to correct it by searching it."
 *
 * --- IT IS A REPORT, AND BOTH VERDICTS ARE NOW RECORDED FROM IT (#618) ---
 *
 * This block used to say there was no merge button here, and that merging
 * belonged on the ground record. The first half was true and the second was
 * not: no such control existed anywhere in the product — not
 * on `/admin/venues`, not on the map's detail dialog — so the page sent a
 * reviewer to a screen that had never been built and the only way to resolve
 * a duplicate was curl against #131's route. Martin, 2026-09-04: "The
 * possible duplicate form also does not have a button to say it is a
 * duplicate and what action needs to be taken."
 *
 * What was right was the CAUTION, and none of it is dropped. There is still
 * no edit control of any kind here, still no bulk action, and the merge is
 * still not a press on a list row: `MergeControl` below opens a step that
 * makes the reviewer choose a survivor explicitly (no default, no
 * pre-selection), shows how many bookmarks, matches and training sessions
 * would move BEFORE anything happens, and then asks a second time. The
 * highest-scoring false positive the scan can produce is two genuinely
 * distinct grounds at one venue — `Oval No.1` / `Oval No.2`, a main ground
 * and its practice field 80 m away — and every one of those gates exists
 * because a mis-tap on that pair rewrites other people's history with no
 * undo. Detection is deterministic; the decision is still a person's, made
 * with both records in front of them.
 *
 * The merge itself is still #131's `POST /api/grounds/:id/merge` — admin
 * gated, unchanged, and called for the first time by `lib/groundMerge.js`.
 * #618 built the affordance, not a second copy of the verb.
 *
 * --- WHAT IT WILL NOT SHOW ---
 *
 * Only PUBLIC grounds are scanned (`is_global OR is_verified` — the same
 * set `GET /api/grounds` already serves every signed-in user), so nothing
 * on this screen is anything a player could not already read. Martin's
 * no-backfill decision (#129) leaves pre-change rows private to their
 * creator, and an admin report that listed them would publish through the
 * back door exactly what that decision keeps private. That filter lives in
 * `api/src/db/repos/groundDuplicates.ts`, on the ROWS before any pair is
 * formed — a mixed public/private pair leaks the private half just as
 * completely as a private pair does.
 *
 * --- TWO VERDICTS, AND THEY ARE NOT THE SAME KIND OF ACT ---
 *
 * "Not a duplicate" (#407) records that a person looked at two records and
 * judged them distinct. It changes NO ground, it is undone by "Put back in
 * the report" on the same screen, and it expires by itself if either ground
 * is later edited into something the judgement was not about — in which case
 * the pair returns, carrying what was decided before.
 *
 * "These are the same ground" (#618) is the other half, and it is a
 * different weight of act entirely: it repoints other people's bookmarks and
 * rewrites the ground on their matches and training sessions, and there is
 * no undo in the app. That asymmetry is why the two controls do not look
 * alike. The dismissal is one press plus an optional note; the merge is a
 * step that will not proceed without an explicit survivor and a preview of
 * what moves, and then asks again.
 *
 * --- THE HONEST FOOTNOTE, RENDERED, NOT JUST WRITTEN DOWN ---
 *
 * A report that quietly misses a class of duplicate is worse than no
 * report, because an empty screen reads as "there are none". So the page
 * says out loud what the scan looks at — and the pairs it is holding back
 * are LISTED AND REACHABLE, COLLAPSED BY DEFAULT: the count rides in the
 * scan summary at the top ("12 pairs to review · 3 held back") and the
 * cards themselves are one press away, with who ruled each pair out and the
 * way back. A suppression nobody can see is indistinguishable from a scan
 * that missed something, which is the same failure this footnote has always
 * been about.
 *
 * #619 IS WHY IT COLLAPSED, AND WHY IT IS STILL HERE. Martin, 2026-09-04:
 * "The 6 grounds that is not a duplicate remains at the bottom of the page.
 * This should disappear from the page." He is right about the cost — the
 * proposed list is capped (`MAX_CANDIDATES`) and the dismissed list is
 * NOT, so every correct "Not a duplicate" made this page permanently
 * longer and none of the added length was work. He is wrong that it should
 * go, for one narrow reason: this section is the only place in the product
 * where a ruled-out pair is visible or reversible — `restoreDuplicatePair`
 * has exactly one caller — so deleting it would put a mis-pressed verdict,
 * including one on a pair that really is a duplicate, permanently out of
 * reach of the interface. The guarantee #407 asked for costs ONE LINE, not
 * N cards; it never required the cards to be expanded on arrival.
 *
 * THE OPEN STATE IS NOT PERSISTED, deliberately (#619 item 3). Every load
 * starts collapsed: the report is the page's job, and the audit record is a
 * thing you go and look at.
 */

/**
 * ONE HALF OF A PAIR, AND THE WAY INTO ITS RECORD (issue #616).
 *
 * Martin, 2026-09-04: "I cannot find some of these — for example United
 * Cricket Club is not showing up as a ground when I search for it." The
 * report named two grounds and then left him to find them by typing a name
 * into `/admin/venues`, where the Provenance filter he had been working in
 * for two days ("Private (unpublished)", #606) guarantees a nil result for
 * every ground on this page — this report only ever lists PUBLIC grounds.
 *
 * The name is the link, so opening a record is one press on the thing the
 * reviewer is already reading, and `venueRecordPath` is the only builder of
 * that URL — the destination reads the same constant back
 * (`VENUE_FOCUS_PARAM`), so a rename cannot leave the report pointing at a
 * parameter nobody reads. Following it lands on a list with the search box
 * and all five filters widened and the page the row is on, which is what
 * makes "in one press" true rather than "in one press plus whatever the
 * last task left set".
 *
 * `min-h-11` is the 44px tap target this repo's controls use (#615), and
 * `break-words` keeps a long venue name inside the card on a 360px phone
 * rather than pushing the pair sideways.
 */
function GroundCard({ ground, testId }) {
  return (
    <div className="flex-1 min-w-0 rounded-lg border border-slate-200 p-3" data-testid={testId}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Link
            to={venueRecordPath(ground.ground_id)}
            className="flex min-h-11 items-center gap-1 font-display font-bold text-slate-900 text-sm underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
            aria-label={`Open the record for ${ground.name}`}
            data-testid={`${testId}-open`}
          >
            <span className="min-w-0 break-words">{ground.name}</span>
            <ExternalLink size={12} className="shrink-0 text-slate-400" aria-hidden="true" />
          </Link>
          <div className="text-xs text-slate-500 mt-0.5 break-words">
            {ground.city}, {ground.country}
          </div>
        </div>
        {ground.is_verified && (
          <BadgeCheck size={16} className="text-emerald-600 shrink-0" aria-label="Verified" />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <Badge variant="outline" className="text-[10px] font-medium">
          {placeTypeLabel(ground.place_type)}
        </Badge>
        <span className="text-[11px] text-slate-500 tabular-nums break-all">
          {Number(ground.latitude).toFixed(5)}, {Number(ground.longitude).toFixed(5)}
        </span>
      </div>
    </div>
  );
}

/** Both records side by side, with the distance between the pins. Shared by
 * a proposed pair and a dismissed one — the reviewer is looking at the same
 * two things either way, and only the action differs. */
function PairBody({ pair }) {
  return (
    <>
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        <GroundCard ground={pair.a} testId="dupe-pair-a" />
        <div className="flex sm:flex-col items-center justify-center gap-1 shrink-0">
          <ArrowRight size={16} className="text-slate-400 rotate-90 sm:rotate-0" />
          <span className="text-[11px] font-semibold text-slate-600 tabular-nums whitespace-nowrap">
            {formatMetres(pair.distance_metres)}
          </span>
        </div>
        <GroundCard ground={pair.b} testId="dupe-pair-b" />
      </div>
      <p className="text-xs text-slate-500 mt-3 break-words" data-testid="dupe-pair-why">
        {explainPair(pair)}
      </p>
    </>
  );
}

/**
 * The "not a duplicate" control, closed until it is pressed.
 *
 * It asks for an optional reason before it writes anything. One click with
 * no confirmation would be the wrong shape for a judgement other admins are
 * meant to trust — and the note is the difference between "somebody ruled
 * this out" and "somebody ruled this out because they are two squares at one
 * park", which is what stops the next reviewer re-doing the work.
 */
function DismissControl({ busy, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs min-h-11"
        disabled={busy}
        onClick={() => setOpen(true)}
        data-testid="dupe-dismiss-open"
      >
        Not a duplicate
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 w-full" data-testid="dupe-dismiss-form">
      <Input
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why are they different? (optional)"
        className="text-xs h-8 flex-1 min-w-[12rem]"
        aria-label="Why are they different?"
        data-testid="dupe-dismiss-note"
      />
      <Button
        type="button"
        size="sm"
        className="text-xs"
        disabled={busy}
        onClick={() => onConfirm(note.trim())}
        data-testid="dupe-dismiss-confirm"
      >
        Record
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-xs"
        disabled={busy}
        onClick={() => setOpen(false)}
        data-testid="dupe-dismiss-cancel"
      >
        Cancel
      </Button>
    </div>
  );
}

/**
 * Issue #618 — "THESE ARE THE SAME GROUND", AND THE THREE THINGS THAT STAND
 * BETWEEN PRESSING IT AND REWRITING OTHER PEOPLE'S RECORDS.
 *
 * This is the most destructive verb in the product. A merge repoints
 * bookmarks that belong to other people, rewrites the ground on their
 * matches and training sessions, moves the venue's surfaces, and retires one
 * of the two records out of the directory — and there is no unmerge. The
 * report shipped read-only (#132) for exactly that reason, and the caution
 * was right; what was wrong was that the footnote sent the reviewer to a
 * control on the ground record that had never been built, so the only way to
 * resolve a duplicate was curl.
 *
 * So the entry point is here, where the reviewer already is, and the three
 * gates are:
 *
 *   1. THE FIRST PRESS MERGES NOTHING. It opens a step. `DismissControl`
 *      beside it has the same shape for a far smaller act.
 *
 *   2. THE SURVIVOR IS CHOSEN, NEVER DEFAULTED. `survivorId` starts `null`
 *      and no branch here sets it except a press on one of the two records.
 *      The merge button is disabled until `canMerge` — one expression,
 *      shared with the request's own guard (`lib/groundMerge.js`) — says a
 *      choice has been made. The scan's top false positive is two genuinely
 *      distinct grounds at ONE venue, scored near-certain, and a
 *      pre-selected survivor is what turns that into a one-tap fusion of two
 *      real grounds.
 *
 *   3. NOTHING IS CONFIRMED BLIND. The impact preview
 *      (`GET /api/admin/grounds/duplicates/impact/:a/:b`) is fetched when the
 *      step opens, and the merge stays disabled until it arrives. A failed
 *      preview offers a retry and refuses the merge: "we could not find out
 *      how much history this moves" is a reason to stop, not a reason to
 *      proceed with a blank where the number goes.
 *
 * WHY NOT A DIALOG. Martin's #613: a control rendered far from the finger
 * that opened it reads as nothing having happened, on the phone he actually
 * works from. This expands in place, inside the card whose two records it is
 * about, so the pair stays on screen while the choice is made.
 */
function MergeControl({ pair, busy, onMerged }) {
  const [open, setOpen] = useState(false);
  // NO DEFAULT. See gate 2 above, and `mergeDirection`, which has no
  // fallback branch to reach.
  const [survivorId, setSurvivorId] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactFailed, setImpactFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  const aId = pair.a.ground_id;
  const bId = pair.b.ground_id;

  const loadImpact = useCallback(() => {
    setImpact(null);
    setImpactFailed(false);
    let cancelled = false;
    fetchPairMergeImpact(aId, bId)
      .then((data) => {
        if (!cancelled) setImpact(data);
      })
      .catch(() => {
        if (!cancelled) setImpactFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [aId, bId]);

  useEffect(() => {
    if (!open) return undefined;
    return loadImpact();
  }, [open, loadImpact]);

  function close() {
    setOpen(false);
    setConfirming(false);
    // The choice is dropped with the step. A survivor left selected behind a
    // closed panel is a pre-selection the next press inherits, which is the
    // one thing gate 2 forbids.
    setSurvivorId(null);
    setError("");
  }

  const direction = mergeDirection(pair, survivorId);
  const ready = canMerge(pair, survivorId) && impact !== null;

  async function runMerge() {
    if (!direction || working) return;
    setWorking(true);
    setError("");
    try {
      const result = await mergeGroundPair({
        loserId: direction.loser.ground_id,
        survivorId: direction.survivor.ground_id,
      });
      onMerged(result);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "That merge did not go through. Reload the report and look at the pair again.",
      );
      setConfirming(false);
      setWorking(false);
    }
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs min-h-11"
        disabled={busy}
        onClick={() => setOpen(true)}
        data-testid="dupe-merge-open"
      >
        <Merge size={13} className="mr-1" />
        These are the same ground
      </Button>
    );
  }

  return (
    <div
      className="w-full rounded-lg border border-amber-200 bg-amber-50/60 p-3"
      data-testid="dupe-merge-step"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="text-amber-700 shrink-0 mt-0.5" />
        <p className="text-xs font-semibold text-amber-900 break-words">
          Which record survives? The other one leaves the directory and everything pointing at it moves
          across.
        </p>
      </div>

      {/* The two records as the two choices, each on its own line below
          `sm` (`basis-full`) — #615's proof, not a mitigation: a flex item
          with `flex-basis:100%` in a wrapping container is alone on its
          line, so a long venue name keeps the full content box on a 360px
          phone instead of being squeezed into a two-character column by the
          button beside it. */}
      <div className="mt-3 flex flex-wrap gap-2" data-testid="dupe-merge-choices">
        {survivorChoices(pair).map((g) => {
          const chosen = survivorId === g.ground_id;
          return (
            <button
              key={g.ground_id}
              type="button"
              aria-pressed={chosen}
              disabled={working}
              onClick={() => {
                setSurvivorId(g.ground_id);
                setConfirming(false);
              }}
              className={`basis-full sm:basis-0 sm:flex-1 min-w-0 text-left rounded-md border px-3 py-2 min-h-11 transition ${
                chosen
                  ? "border-[#0B2E59] bg-white ring-2 ring-[#0B2E59]"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid={`dupe-merge-keep-${g.ground_id}`}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {chosen ? "Keeping" : "Keep"}
              </span>
              <span className="block font-display font-bold text-sm text-slate-900 break-words">
                {g.name}
              </span>
              <span className="block text-[11px] text-slate-500 break-words">
                {describeSurvivorChoice(g)}
              </span>
            </button>
          );
        })}
      </div>

      {!survivorId && (
        <p className="text-[11px] text-amber-800 mt-2 break-words" data-testid="dupe-merge-unchosen">
          Nothing is chosen for you. Two different grounds at one venue — Oval No.1 and Oval No.2, a field
          and its practice ground — look exactly like this pair, so pick the record you have checked.
        </p>
      )}

      {/* WHAT MOVES, BEFORE THE PRESS. Rendered as soon as a survivor is
          picked, and again inside the confirm below, because the confirm is
          the step the acceptance criterion is about and a number that
          scrolled off screen is a number that was not shown. */}
      {impactFailed && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="dupe-merge-impact-failed">
          <span className="text-xs text-rose-700 break-words">
            Could not work out how much history this would move, so the merge is held back.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => loadImpact()}
            data-testid="dupe-merge-impact-retry"
          >
            Try again
          </Button>
        </div>
      )}
      {!impactFailed && impact === null && (
        <p className="text-xs text-slate-500 mt-3" data-testid="dupe-merge-impact-loading">
          Counting what would move…
        </p>
      )}
      {impact !== null && direction && (
        <div className="mt-3" data-testid="dupe-merge-impact">
          <p className="text-xs font-semibold text-slate-700 break-words">
            Merging “{direction.loser.name}” moves:
          </p>
          <ul className="mt-1 space-y-0.5">
            {describeMergeImpact(impact, direction.loser.ground_id).map((line) => (
              <li key={line} className="text-xs text-slate-600 break-words">
                • {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-rose-700 break-words" role="alert" data-testid="dupe-merge-error">
          {error}
        </p>
      )}

      {/* Step two. `FacilityDeleteControl`'s shape (`AdminVenues.jsx`), for
          the same reason and a larger one: that control's delete is refused
          by the API the moment anything references the surface, and this one
          is not refused by anything. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!confirming ? (
          <>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="text-xs"
              disabled={!ready || working || busy}
              onClick={() => setConfirming(true)}
              data-testid="dupe-merge-confirm-open"
            >
              Merge these grounds
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              disabled={working}
              onClick={close}
              data-testid="dupe-merge-cancel"
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span
              className="basis-full text-xs font-semibold text-rose-800 break-words"
              data-testid="dupe-merge-confirm-warning"
            >
              {describeMergeDirection(direction)} {MERGE_UNDO_WARNING}
            </span>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="text-xs"
              disabled={!ready || working}
              onClick={runMerge}
              data-testid="dupe-merge-confirm"
            >
              {working ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
              Yes, merge them
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              disabled={working}
              onClick={() => setConfirming(false)}
              data-testid="dupe-merge-confirm-cancel"
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * #1152 — the pair's map, mounted when its card first scrolls near the
 * viewport and kept after that. A report holds up to 200 pairs; mounting a
 * Leaflet map for each on load would be 200 tile layers. Without
 * `IntersectionObserver` (old browsers, jsdom) it mounts at once.
 */
function LazyPairMap({ pair }) {
  const holder = useRef(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (seen || !holder.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(holder.current);
    return () => observer.disconnect();
  }, [seen]);
  return (
    <div ref={holder} className="mt-3" style={seen ? undefined : { minHeight: 180 }} data-testid="dupe-pair-map-slot">
      {seen && (
        <PairMap
          a={{ lat: pair.a.latitude, lng: pair.a.longitude, name: pair.a.name }}
          b={{ lat: pair.b.latitude, lng: pair.b.longitude, name: pair.b.name }}
        />
      )}
    </div>
  );
}

function PairRow({ pair, busy, onDismiss, onMerged }) {
  return (
    <Card className="border-slate-200" data-testid="dupe-pair">
      <CardContent className="p-4">
        <PairBody pair={pair} />
        <LazyPairMap pair={pair} />
        {pair.stale_verdict && (
          <p className="text-xs text-amber-700 mt-2 break-words" data-testid="dupe-pair-stale">
            {formatStaleVerdict(pair.stale_verdict)}
          </p>
        )}
        {/* Both verdicts, side by side — #618. The negative one is first
            and stays first: it is the safe act, it is the one an honest
            reviewer reaches for more often, and the destructive verb should
            not be where a thumb lands by habit. `MergeControl` expands to a
            full-width panel, so on a phone the step opens on its own line
            directly under the two records it is about. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <DismissControl busy={busy} onConfirm={(note) => onDismiss(pair, note)} />
          <MergeControl pair={pair} busy={busy} onMerged={onMerged} />
        </div>
      </CardContent>
    </Card>
  );
}

/** A pair already ruled out: the same two records, the judgement, and the
 * way back. Rendered rather than hidden — see the page header. */
function DismissedRow({ pair, busy, onRestore }) {
  return (
    <Card className="border-slate-200 bg-slate-50" data-testid="dupe-dismissed-pair">
      <CardContent className="p-4">
        <PairBody pair={pair} />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-600 break-words" data-testid="dupe-dismissed-verdict">
            {formatVerdict(pair.verdict)}
            {pair.verdict?.note ? ` — ${pair.verdict.note}` : ""}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            disabled={busy}
            onClick={() => onRestore(pair)}
            data-testid="dupe-restore"
          >
            <Undo2 size={13} className="mr-1" />
            Put back in the report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminGroundDuplicates() {
  const [report, setReport] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionFailed, setActionFailed] = useState("");
  /** #618 — the merge's own counts, kept after the refetch that removes the
   * pair. Without it the only feedback for the most destructive verb here is
   * a card silently disappearing, which is indistinguishable from a merge
   * that touched nothing. */
  const [mergeResult, setMergeResult] = useState(null);
  /** #619 — the ruled-out list, closed on arrival and NOT remembered. Every
   * load starts collapsed: the page's job is the report, and the audit
   * record is a thing you go and look at. */
  const [showDismissed, setShowDismissed] = useState(false);

  const retry = useCallback(() => {
    setLoaded(false);
    setLoadFailed(false);
    setReloadKey((k) => k + 1);
  }, []);

  /**
   * Both writes refetch rather than editing the list in place. The scan
   * decides what is proposed, what is suppressed and what has expired, and a
   * client that moved a card between two arrays would be a second, shorter
   * copy of that rule — the one that quietly disagrees the first time either
   * side is tuned.
   */
  const runAction = useCallback(async (action, failure) => {
    setBusy(true);
    setActionFailed("");
    try {
      await action();
      setReloadKey((k) => k + 1);
    } catch {
      setActionFailed(failure);
    } finally {
      setBusy(false);
    }
  }, []);

  const onDismiss = useCallback(
    (pair, note) =>
      runAction(
        () => dismissDuplicatePair(pair.a.ground_id, pair.b.ground_id, note),
        "That pair could not be recorded as different. It may have been merged or edited since this page loaded — reload and look again.",
      ),
    [runAction],
  );

  /** #618. The merge control owns its own busy and error state — it is a
   * multi-step control, not a one-press action like the two above — so the
   * page's job is only to record what happened and re-run the scan. The pair
   * leaves the report by construction (`merged_into IS NULL`), so the
   * refetch is what removes it; nothing is spliced out of a local array. */
  const onMerged = useCallback((result) => {
    setActionFailed("");
    setMergeResult(result ?? {});
    setReloadKey((k) => k + 1);
  }, []);

  const onRestore = useCallback(
    (pair) =>
      runAction(
        () => restoreDuplicatePair(pair.a.ground_id, pair.b.ground_id),
        "That pair could not be put back. Reload and try again.",
      ),
    [runAction],
  );

  useEffect(() => {
    let cancelled = false;
    fetchDuplicateGroundReport()
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        setLoadFailed(false);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const groups = groupByBand(report?.pairs);

  return (
    <div className="space-y-6" data-testid="admin-ground-duplicates-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Admin · Grounds</p>
        <h1 className="font-display font-black text-2xl text-slate-900 mt-1">Possible duplicates</h1>
        <p className="text-slate-500 text-sm mt-1">
          Pairs of public grounds that look like the same field recorded twice. Open either record by
          pressing its name; the map shows both pins.
        </p>
      </div>

      {actionFailed && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4 text-sm text-rose-700 break-words" data-testid="dupe-action-failed">
            {actionFailed}
          </CardContent>
        </Card>
      )}

      {mergeResult && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-700 shrink-0 mt-0.5" />
            <span className="text-sm text-emerald-800 break-words" data-testid="dupe-merge-done">
              {describeMergeResult(mergeResult)}
            </span>
          </CardContent>
        </Card>
      )}

      {loadFailed && (
        <Card className="border-slate-200">
          <CardContent className="p-4 flex">
            <LoadFailureNote what="the duplicate report" onRetry={retry} testId="dupe-load-failed" />
          </CardContent>
        </Card>
      )}

      {loaded && !loadFailed && (
        <>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "#0B2E5915" }}
                >
                  <MapPin size={16} color="#0B2E59" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900" data-testid="dupe-scan-summary">
                    {formatScanSummary(report)}
                  </div>
                  {/* #619 — the held-back count rides here, at the top,
                      where it is read without expanding anything. #407's
                      guarantee is that a suppression is VISIBLE; a count
                      inside a collapsed section would not be. */}
                  <div
                    className="text-sm font-semibold text-slate-700 mt-0.5 break-words"
                    data-testid="dupe-scan-counts"
                  >
                    {formatReportCounts(report)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 break-words">
                    Private grounds are never scanned or shown here. Grounds of different kinds — a field and
                    its nets — are never paired, however close or alike they are.
                  </div>
                  {report?.truncated && (
                    <div className="text-xs text-amber-700 mt-1 break-words">
                      Showing the strongest {report.max_candidates} pairs only; there are more.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {groups.length === 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-6 text-center">
                <div className="text-sm font-semibold text-slate-900">No candidate pairs</div>
                <p className="text-xs text-slate-500 mt-1 break-words">
                  Nothing in the public directory scored high enough to be worth your time. This checks names
                  (ignoring case, accents and punctuation) and the real distance between pins — it cannot spot
                  a duplicate that was given an unrelated name and a pin more than 100 m away.
                </p>
              </CardContent>
            </Card>
          )}

          {groups.map((group) => (
            <div key={group.key} className="space-y-3" data-testid={`dupe-band-${group.key}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <Badge variant="outline" className={`text-[11px] font-semibold ${group.tone}`}>
                  {group.label}
                </Badge>
                <span className="text-xs text-slate-500 tabular-nums">
                  {group.pairs.length} {group.pairs.length === 1 ? "pair" : "pairs"}
                </span>
                <span className="text-xs text-slate-500 basis-full break-words">{group.blurb}</span>
              </div>
              {group.pairs.map((pair) => (
                <PairRow
                  key={`${pair.a.ground_id}-${pair.b.ground_id}`}
                  pair={pair}
                  busy={busy}
                  onDismiss={onDismiss}
                  onMerged={onMerged}
                />
              ))}
            </div>
          ))}

          {/* #619 — the footnote sat AFTER an unbounded list, which is the
              wrong end of the page for the sentence that explains the page.
              It now closes the report, and the audit record follows it. */}
          <p className="text-xs text-slate-400 break-words" data-testid="dupe-caveat">
            Merging keeps one record and retires the other: bookmarks, matches, training sessions and
            recorded surfaces move to the ground you keep, and the retired one leaves the directory. It
            cannot be undone from here, so you choose which record survives and see what moves before it
            runs. Marking a pair <em>not</em> a duplicate changes neither ground: it records that you looked,
            holds the pair back from this list, and can be undone. If either ground is edited afterwards, the
            pair returns for another look. Archived venues are not scanned at all.
          </p>

          {/* #619 — COLLAPSED, NOT CUT. The header row is the whole of
              #407's guarantee: the badge, the count, and the sentence
              saying why these are missing from the list above. The CARDS
              are what grew without bound (`dismissed` has no cap, while
              the proposed list has `MAX_CANDIDATES`), and they are what is
              now one press away — including "Put back in the report",
              which is the only route in the product back from a verdict. */}
          {(report?.dismissed?.length ?? 0) > 0 && (
            <div className="space-y-3" data-testid="dupe-dismissed-section">
              <div className="flex flex-wrap items-baseline gap-2">
                <Badge variant="outline" className="text-[11px] font-semibold bg-slate-100 text-slate-600 border-slate-200">
                  Ruled out
                </Badge>
                <span className="text-xs text-slate-500 tabular-nums" data-testid="dupe-dismissed-count">
                  {report.dismissed.length} {report.dismissed.length === 1 ? "pair" : "pairs"}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  aria-expanded={showDismissed}
                  aria-controls="dupe-dismissed-list"
                  onClick={() => setShowDismissed((open) => !open)}
                  data-testid="dupe-dismissed-toggle"
                >
                  {showDismissed ? (
                    <ChevronDown size={13} className="mr-1" />
                  ) : (
                    <ChevronRight size={13} className="mr-1" />
                  )}
                  {showDismissed ? "Hide" : "Show"}
                </Button>
                <span className="text-xs text-slate-500 basis-full break-words">
                  Someone looked at these and decided they are genuinely different fields, so they are held
                  back from the list above. Edit either ground and the pair comes back for another look.
                </span>
              </div>
              {showDismissed && (
                <div className="space-y-3" id="dupe-dismissed-list" data-testid="dupe-dismissed-list">
                  {report.dismissed.map((pair) => (
                    <DismissedRow
                      key={`${pair.a.ground_id}-${pair.b.ground_id}`}
                      pair={pair}
                      busy={busy}
                      onRestore={onRestore}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
